import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, NEVER, Observable, Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { PassengerNameRecord } from '@fcom/common-booking';
import { CmsArticle, CmsCollection, CmsImageData } from '@fcom/core-api';
import { FragmentService } from '@fcom/common';
import { TourOperator } from '@fcom/common-booking/interfaces/tour-operator.interface';
import { ETICKET_PATTERN, NAME_PATTERN, PNR_PATTERN_WITH_TRAILING_WHITESPACE } from '@fcom/core/utils';
import { ButtonType, LoaderTheme, ModalButtons, NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { RequestInitiator, TourOperatorCode } from '@fcom/dapi/api/models';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';

import { MmbError } from '../../../../enums';
import { ManageBookingFeatureState } from '../../../../interfaces';
import { ManageBookingService } from '../../../../services';
import { getError, StateActions } from '../../../../store';
import { PnrFormService } from '../../../../services/pnr-form-service';

@Component({
  selector: 'fin-mmb-entry-widget',
  templateUrl: './manage-booking-entry-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageBookingEntryWidgetComponent implements OnInit, OnDestroy {
  readonly MmbError = MmbError;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly ButtonType = ButtonType;
  readonly LoaderTheme = LoaderTheme;

  @Input() backgroundImage: CmsImageData;
  @Input() tourOperatorImage: CmsImageData;
  @Input() loading: boolean;
  @Input() operator: TourOperator | undefined;
  @Input() disruptions$: Observable<CmsCollection> = NEVER;

  recLocRegex = new RegExp(PNR_PATTERN_WITH_TRAILING_WHITESPACE.source + '|' + ETICKET_PATTERN.source);

  mainError$: Observable<string>;
  groupModalError$: Observable<MmbError>;
  showGroupBookingModal$ = new BehaviorSubject(false);

  GROUP_ERRORS = [MmbError.GROUP_BOOKING, MmbError.GROUP_BOOKING_NOT_FOUND];
  GROUP_MODAL_ERRORS = [MmbError.GROUP_BOOKING_NOT_FOUND, MmbError.GROUP_BOOKING_LOGGING_WITH_INFANT];

  cancelRetrieve$: Subject<boolean> = new Subject<boolean>();

  cmsArticle$: Observable<CmsArticle>;

  initialValues$: Observable<PassengerNameRecord>;

  groupFirstNameForm: FormGroup<{
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    recLoc: FormControl<string>;
    tourOperatorCode: FormControl<TourOperatorCode>;
  }>;

  NAME_VALIDATORS = [
    Validators.required,
    Validators.minLength(2),
    Validators.maxLength(50),
    Validators.pattern(NAME_PATTERN),
  ];

  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<ManageBookingFeatureState>,
    private mmbService: ManageBookingService,
    private fragmentService: FragmentService,
    private feedbackService: FeedbackService,
    private pnrFormService: PnrFormService
  ) {}

  ngOnInit(): void {
    this.cmsArticle$ = this.fragmentService.getFragment('MMB.fragments.mmbInfo.url');

    const error$ = this.store$.pipe(getError());
    this.subscriptions.add(
      error$.subscribe((error) => {
        if (this.GROUP_ERRORS.includes(error)) {
          this.showGroupBookingModal$.next(true);
        }
      })
    );

    this.groupModalError$ = error$.pipe(filter((error) => error && this.GROUP_MODAL_ERRORS.includes(error)));
    this.mainError$ = combineLatest([this.showGroupBookingModal$, error$]).pipe(
      filter(([show]) => !show),
      map(([_, error]) => (error ? 'MMB.login.errors.' + error : null))
    );
    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);

    const { initialValues$, deepLinkRouteChange$ } = this.pnrFormService.getInitialValues();

    this.initialValues$ = initialValues$;

    this.subscriptions.add(
      deepLinkRouteChange$.subscribe((record: PassengerNameRecord) => {
        this.onSubmit(record, RequestInitiator.DEEPLINK_MMB);
      })
    );
  }

  submitGroupFirstName(): void {
    if (this.groupFirstNameForm.valid) {
      this.onSubmit(this.groupFirstNameForm.value as PassengerNameRecord);
    }
  }

  onSubmit(event: PassengerNameRecord | null, requestInitiator = RequestInitiator.MMB): void {
    this.groupFirstNameForm = new FormGroup({
      firstName: new FormControl(event.firstName ?? '', this.NAME_VALIDATORS),
      lastName: new FormControl(event.lastName),
      recLoc: new FormControl(event.recLoc),
      tourOperatorCode: new FormControl(event.tourOperatorCode),
    });
    this.mmbService.retrieveOrderFromForm(event, requestInitiator, true, true, this.cancelRetrieve$, false);
  }

  cancelRetrieve(): void {
    this.store$.dispatch(StateActions.setLoading({ loading: false }));
    this.cancelRetrieve$.next(true);
  }

  onFirstNameModalClose(): void {
    this.groupFirstNameForm.controls.firstName.setValue('');
    this.store$.dispatch(StateActions.clearError());
    this.showGroupBookingModal$.next(false);
  }

  closeErrorNotification(): void {
    this.store$.dispatch(StateActions.clearError());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
